import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { SENSORS_URL } from '../Constants';
import './NumberInput.css';
import { resolveValueColorFromStops } from './liquidShared';

const NumberInput = ({
  title,
  transparentBackground,
  min,
  max,
  step,
  value,
  unit,
  showUnit = false,
  loopValues,
  color,
  buttonBgColor,
  buttonHoverBgColor,
  valueColor,
  valueHoverColor,
  changeColorBasedOnValue = false,
  colorGrading = false,
  colors = [],
  datastreamColor,
  draggable,
  pin,
  did,
  tid,
  contentAlignment,
}) => {
  const containerRef = useRef(null);

  const resolveControlColor = (v) => {
    const s = String(v || '').trim();
    return s ? s : null;
  };

  const withAlpha = (hex, alpha = 0.15) => {
    const h = String(hex || '').trim();
    if (!h) return null;
    const m = h.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
    if (!m) return null;
    let raw = m[1];
    if (raw.length === 3) raw = raw.split('').map(ch => ch + ch).join('');
    const r = parseInt(raw.slice(0, 2), 16);
    const g = parseInt(raw.slice(2, 4), 16);
    const b = parseInt(raw.slice(4, 6), 16);
    const a = Math.max(0, Math.min(1, Number(alpha)));
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  const resolvedControlColor = useMemo(() => {
    return resolveControlColor(color) || resolveControlColor(datastreamColor) || null;
  }, [color, datastreamColor]);

    const resolvedBtnTextColor = useMemo(() => {
    // Text color for + / - follows widget `color` (or datastream color)
    return resolvedControlColor || null;
  }, [resolvedControlColor]);

  const resolvedBtnBgColor = useMemo(() => {
    // 1) explicit widget setting
    // 2) a subtle alpha version of the datastream/widget main color (only if hex)
    return resolveControlColor(buttonBgColor) || withAlpha(resolvedControlColor, 0.15) || null;
  }, [buttonBgColor, resolvedControlColor]);

  const resolvedBtnHoverBgColor = useMemo(() => {
    return (
      resolveControlColor(buttonHoverBgColor) ||
      withAlpha(resolvedControlColor, 0.28) ||
      resolvedBtnBgColor ||
      null
    );
  }, [buttonHoverBgColor, resolvedControlColor, resolvedBtnBgColor]);

  const resolvedValueColor = useMemo(() => {
    return resolveControlColor(valueColor) || resolvedBtnTextColor || null;
  }, [valueColor, resolvedBtnTextColor]);

  const resolvedValueHoverColor = useMemo(() => {
    return resolveControlColor(valueHoverColor) || null;
  }, [valueHoverColor]);

  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
  const toNumber = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const hasMin = useMemo(() => Number.isFinite(Number(min)), [min]);
  const hasMax = useMemo(() => Number.isFinite(Number(max)), [max]);

  const numericMin = useMemo(() => (hasMin ? Number(min) : -Infinity), [hasMin, min]);

  const numericMax = useMemo(() => {
    if (!hasMax) return Infinity;
    const mx = Number(max);
    if (hasMin) {
      const mn = Number(min);
      return mx >= mn ? mx : mn;
    }
    return mx;
  }, [hasMax, max, hasMin, min]);

  const numericStep = useMemo(() => {
    const s = toNumber(step, 1);
    return Number.isFinite(s) && s > 0 ? s : 1;
  }, [step]);

  const baseForValue = useMemo(() => (hasMin ? Number(min) : 0), [hasMin, min]);

  const [numberValue, setNumberValue] = useState(String(toNumber(value, baseForValue)));
  const [edit, setEdit] = useState(false);
  const [cursorState, setCursorState] = useState('idle');

  const lastCommittedRef = useRef(String(toNumber(value, baseForValue)));
  const pendingStreamValueRef = useRef(null);

  useEffect(() => {
    const v = String(toNumber(value, baseForValue));

    if (edit) {
      pendingStreamValueRef.current = v;
      return;
    }

    setNumberValue(v);
    lastCommittedRef.current = v;
    pendingStreamValueRef.current = null;
  }, [value, baseForValue, edit]);

  const activeNumericValue = useMemo(() => {
    const local = Number(numberValue);
    if (Number.isFinite(local)) return local;

    const incoming = Number(value);
    if (Number.isFinite(incoming)) return incoming;

    return null;
  }, [numberValue, value]);

  const dynamicValueColor = useMemo(() => {
    if (activeNumericValue == null || !Number.isFinite(activeNumericValue)) {
      return null;
    }

    return resolveValueColorFromStops({
      value: clamp(activeNumericValue, numericMin, numericMax),
      baseColor: null,
      stops: colors,
      enabled: changeColorBasedOnValue,
      grading: colorGrading,
    });
  }, [
    changeColorBasedOnValue,
    colors,
    activeNumericValue,
    numericMin,
    numericMax,
    colorGrading,
  ]);

  const activeValueColor = useMemo(
    () => dynamicValueColor || resolvedValueColor,
    [dynamicValueColor, resolvedValueColor]
  );

  useEffect(() => {
    if (!containerRef.current) return;
    if (resolvedControlColor) {
      containerRef.current.style.setProperty("--numberinput-control-color", resolvedControlColor);
    } else {
      containerRef.current.style.removeProperty("--numberinput-control-color");
    }

    if (resolvedBtnTextColor) {
      containerRef.current.style.setProperty("--numberinput-button-color", resolvedBtnTextColor);
      containerRef.current.style.setProperty("--numberinput-button-border-color", resolvedBtnTextColor);
    } else {
      containerRef.current.style.removeProperty("--numberinput-button-color");
      containerRef.current.style.removeProperty("--numberinput-button-border-color");
    }

    if (resolvedBtnBgColor) {
      containerRef.current.style.setProperty("--numberinput-button-bg", resolvedBtnBgColor);
    } else {
      containerRef.current.style.removeProperty("--numberinput-button-bg");
    }

    if (resolvedBtnHoverBgColor) {
      containerRef.current.style.setProperty("--numberinput-button-hover-bg", resolvedBtnHoverBgColor);
    } else {
      containerRef.current.style.removeProperty("--numberinput-button-hover-bg");
    }

    if (activeValueColor) {
      containerRef.current.style.setProperty("--numberinput-value-color", activeValueColor);
    } else {
      containerRef.current.style.removeProperty("--numberinput-value-color");
    }

    if (resolvedValueHoverColor) {
      containerRef.current.style.setProperty("--numberinput-value-hover-color", resolvedValueHoverColor);
    } else {
      containerRef.current.style.removeProperty("--numberinput-value-hover-color");
    }
  }, [resolvedControlColor, resolvedBtnTextColor, resolvedBtnBgColor, resolvedBtnHoverBgColor, activeValueColor, resolvedValueHoverColor]);

  const pushToDevice = async (nextStr) => {
    if (!did) return;
    const body = JSON.stringify({ device: did, template: tid, data: { [pin]: nextStr } });
    try {
      await axios.post(`${SENSORS_URL}/control`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (err) {
      console.log(err?.message || String(err));
    }
  };

  const commitValue = (rawValue) => {
    const parsed = parseFloat(String(rawValue));
    if (!Number.isFinite(parsed)) return;

    const clamped = clamp(parsed, numericMin, numericMax);
    const rounded = Math.round(clamped * 1_000_000) / 1_000_000;
    const nextStr = String(rounded);

    setNumberValue(nextStr);
    lastCommittedRef.current = nextStr;
    pushToDevice(nextStr);
  };

  const handleTypedChange = (raw) => {
    const str = String(raw);

    if (/^-?\d*\.?\d*$/.test(str)) {
      setNumberValue(str);
    }
  };

  const finalizeEdit = () => {
    const str = String(numberValue);

    if (
      str === '' ||
      str === '-' ||
      str === '.' ||
      str === '-.' ||
      !Number.isFinite(parseFloat(str))
    ) {
      setNumberValue(lastCommittedRef.current);
    } else {
      commitValue(str);
    }

    setEdit(false);
  };

  const startEdit = () => {
    setNumberValue(lastCommittedRef.current);
    setEdit(true);
  };

  const canLoop = loopValues && Number.isFinite(numericMin) && Number.isFinite(numericMax);

  const dec = () => {
    const current = toNumber(numberValue, baseForValue);
    let next = current - numericStep;

    if (canLoop) {
      if (next < numericMin) next = numericMax;
    } else if (Number.isFinite(numericMin)) {
      if (next < numericMin) next = numericMin;
    }

    next = Math.round(next * 1_000_000) / 1_000_000;
    commitValue(next);
  };

  const inc = () => {
    const current = toNumber(numberValue, baseForValue);
    let next = current + numericStep;

    if (canLoop) {
      if (next > numericMax) next = numericMin;
    } else if (Number.isFinite(numericMax)) {
      if (next > numericMax) next = numericMax;
    }

    next = Math.round(next * 1_000_000) / 1_000_000;
    commitValue(next);
  };

  const cursorClass = draggable
    ? (cursorState === 'grabbing' ? 'number-input--grabbing' : 'number-input--grab')
    : 'number-input--auto';
  const normalizedContentAlignment = useMemo(() => {
    const nextAlignment = String(contentAlignment || 'auto').trim().toLowerCase();
    return ['auto', 'top', 'center', 'bottom', 'title-line'].includes(nextAlignment)
      ? nextAlignment
      : 'auto';
  }, [contentAlignment]);
  const hasUnit = unit != null && String(unit).trim() !== '';

  return (
    <div
      ref={containerRef}
      className={[
        "widget-container",
        "number-input",
        cursorClass,
        `number-input--align-${normalizedContentAlignment}`,
        transparentBackground ? "number-input--transparent" : "",
      ].filter(Boolean).join(" ")}
      onMouseDown={() => draggable && setCursorState('grabbing')}
      onMouseUp={() => draggable && setCursorState('grab')}
      onMouseLeave={() => draggable && setCursorState('grab')}
      draggable={draggable}
      onDragStart={(e) => {
        const id = e.target.closest('.widget')?.id;
        if (!id) return;

        const rect = e.target.getBoundingClientRect();
        const offsetX = e.clientX - rect.x;
        const offsetY = e.clientY - rect.y;

        let dragData = `number;${id};${offsetX};${offsetY};`;
        dragData += id.startsWith('base') ? 'add' : 'move';
        e.dataTransfer.setData('text/plain', dragData);
      }}
    >
      <div className="number-input__title">
        {title}
      </div>

      <div className="number-input__row the-slider">
        <button
          className="number-input__btn"
          onClick={dec}
          onMouseDown={(e) => e.preventDefault()}
          title="Decrease"
          type="button"
        >
          -
        </button>

        {edit ? (
          <input
            value={numberValue}
            autoFocus
            className="widget-input number-input__editInput"
            inputMode="decimal"
            onBlur={finalizeEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') finalizeEdit();
              if (e.key === 'Escape') {
                setNumberValue(lastCommittedRef.current);
                setEdit(false);
              }
            }}
            onChange={(e) => handleTypedChange(e.target.value)}
          />
        ) : (
          <div
            className="slider-value number-input__value"
            title={numberValue}
            onClick={startEdit}
          >
            <span className="number-input__valueText">
              {numberValue}
              {showUnit && hasUnit ? ` ${String(unit).trim()}` : ''}
            </span>
          </div>
        )}

        <button
          className="number-input__btn"
          onClick={inc}
          onMouseDown={(e) => e.preventDefault()}
          title="Increase"
          type="button"
        >
          +
        </button>
      </div>
    </div>
  );
};

export default NumberInput;
